import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Box, Chip, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { SesVerificationStatus } from "generated/graphql";

interface StatusDisplayProps {
  identity: any;
  refreshLoading: boolean;
  onRefresh: () => void;
  onToggle: (enabled: boolean) => void;
  onSendTestEmail: () => void;
  testEmailLoading: boolean;
}

export default function StatusDisplay({
  identity,
  refreshLoading,
  onRefresh,
  onToggle,
  onSendTestEmail,
  testEmailLoading,
}: StatusDisplayProps) {
  if (!identity) {
    return (
      <Alert severity="warning">
        You have not set up a custom sending domain yet. Emails will be sent from Bitumio&apos;s
        default sender.
      </Alert>
    );
  }

  const { verificationStatus, enabled } = identity;

  if (verificationStatus === SesVerificationStatus.VERIFIED && !enabled) {
    return (
      <Box>
        <Chip label="Disabled" color="default" size="small" sx={{ mb: 1.5 }} />
        <MDTypography variant="body2" mb={2}>
          Custom sending is turned off. Emails will be sent from Bitumio&apos;s default sender.
        </MDTypography>
        <FormControlLabel
          control={<Switch checked={false} onChange={() => onToggle(true)} />}
          label="Enable custom sending"
        />
      </Box>
    );
  }

  if (verificationStatus === SesVerificationStatus.VERIFIED) {
    return (
      <Box>
        <Chip label="Verified" color="success" size="small" sx={{ mb: 1.5 }} />
        <MDTypography variant="body2" mb={2}>
          Your domain is verified. Bitumio will now send emails from your configured address.
        </MDTypography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={<Switch checked={enabled} onChange={() => onToggle(!enabled)} />}
            label={enabled ? "Disable custom sending" : "Enable custom sending"}
          />
          {enabled && (
            <MDButton
              color="info"
              variant="outlined"
              onClick={onSendTestEmail}
              disabled={testEmailLoading}
            >
              {testEmailLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Send test email"
              )}
            </MDButton>
          )}
        </Box>
      </Box>
    );
  }

  if (
    verificationStatus === SesVerificationStatus.FAILED ||
    verificationStatus === SesVerificationStatus.TEMPORARY_FAILURE
  ) {
    return (
      <Box>
        <Chip label="Failed" color="error" size="small" sx={{ mb: 1.5 }} />
        <MDTypography variant="body2" mb={2}>
          We could not verify your domain. Review your DNS records below and try again.
        </MDTypography>
        <MDButton
          color="info"
          variant="outlined"
          onClick={onRefresh}
          disabled={refreshLoading}
          startIcon={refreshLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Refresh status
        </MDButton>
      </Box>
    );
  }

  return (
    <Box>
      <Chip label="Pending" color="warning" size="small" sx={{ mb: 1.5 }} />
      <MDTypography variant="body2" mb={2}>
        Your domain has been added but DNS setup is not complete yet. Add the records below, then
        click Refresh status.
      </MDTypography>
      <MDButton
        color="info"
        variant="outlined"
        onClick={onRefresh}
        disabled={refreshLoading}
        startIcon={refreshLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
      >
        Refresh status
      </MDButton>
    </Box>
  );
}
